interface TreeNodePosition {
  left: number;
  top: number;
}

export interface TreeBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export function getTreeBounds(
  nodes: ReadonlyArray<TreeNodePosition>,
  nodeWidth: number,
  nodeHeight: number,
): TreeBounds {
  if (nodes.length === 0) {
    return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  }

  return nodes.reduce<TreeBounds>(
    (bounds, node) => {
      const left = node.left * (nodeWidth / 2);
      const top = node.top * (nodeHeight / 2);
      const right = left + nodeWidth;
      const bottom = top + nodeHeight;

      bounds.left = Math.min(bounds.left, left);
      bounds.top = Math.min(bounds.top, top);
      bounds.right = Math.max(bounds.right, right);
      bounds.bottom = Math.max(bounds.bottom, bottom);
      bounds.width = bounds.right - bounds.left;
      bounds.height = bounds.bottom - bounds.top;
      return bounds;
    },
    {
      left: Infinity,
      top: Infinity,
      right: -Infinity,
      bottom: -Infinity,
      width: 0,
      height: 0,
    },
  );
}

export function fitEdges({
  drawingWidth,
  drawingHeight,
  bounds,
  setTransform,
}: {
  drawingWidth: number;
  drawingHeight: number;
  bounds: TreeBounds;
  setTransform: (x: number, y: number, scale: number) => void;
}) {
  if (
    drawingWidth <= 0 ||
    drawingHeight <= 0 ||
    bounds.width <= 0 ||
    bounds.height <= 0
  ) {
    return;
  }

  const scale = Math.min(
    drawingWidth / bounds.width,
    drawingHeight / bounds.height,
    1,
  );
  const calculatedPositionX =
    (drawingWidth - bounds.width * scale) / 2 - bounds.left * scale;
  const calculatedPositionY =
    (drawingHeight - bounds.height * scale) / 2 - bounds.top * scale;

  setTransform(calculatedPositionX, calculatedPositionY, scale);
}

export const LARGE_TREE_THRESHOLD = 150;
export const DEFAULT_LARGE_TREE_DEPTH = 3;

interface RelNode {
  id: string;
  parents: readonly { id: string }[];
  children: readonly { id: string }[];
  siblings: readonly { id: string }[];
  spouses: readonly { id: string }[];
}

// Keeps nodes within `maxDepth` generations of the root (spouses/siblings
// don't add a generation) and drops relations pointing to removed nodes.
export function limitNodesByDepth<T extends RelNode>(
  nodes: readonly T[],
  rootId: string | null,
  maxDepth: number | null,
): T[] {
  if (!rootId || maxDepth === null) {
    return nodes as T[];
  }
  const byId = new Map(nodes.map((n) => [n.id, n]));
  if (!byId.has(rootId)) {
    return nodes as T[];
  }

  const dist = new Map<string, number>([[rootId, 0]]);
  let frontier: string[] = [rootId];
  // Process by generation: expand same-generation links first, then step.
  for (let depth = 0; depth <= maxDepth && frontier.length > 0; depth += 1) {
    const stack = [...frontier];
    const next: string[] = [];
    while (stack.length > 0) {
      const node = byId.get(stack.pop() as string);
      if (!node) continue;
      [...node.spouses, ...node.siblings].forEach(({ id }) => {
        if (byId.has(id) && !dist.has(id)) {
          dist.set(id, depth);
          stack.push(id);
        }
      });
      [...node.parents, ...node.children].forEach(({ id }) => {
        if (byId.has(id) && !dist.has(id)) {
          dist.set(id, depth + 1);
          next.push(id);
        }
      });
    }
    frontier = next.filter((id) => (dist.get(id) ?? 0) <= maxDepth);
  }

  const keep = (r: { id: string }) => (dist.get(r.id) ?? Infinity) <= maxDepth;
  return nodes
    .filter((n) => keep(n))
    .map((n) => ({
      ...n,
      parents: n.parents.filter(keep),
      children: n.children.filter(keep),
      siblings: n.siblings.filter(keep),
      spouses: n.spouses.filter(keep),
    }));
}
